<template>
  <div
    class="login-consent"
    :dir="isRtl ? 'rtl' : 'ltr'"
  >
    <!-- 18+ yaş onayı: onaylanana kadar giriş ekranının üstünde kalır -->
    <div
      v-if="showAgeGate"
      class="age-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
    >
      <div class="age-gate__card">
        <div class="age-gate__brand">
          <img
            :src="logo"
            alt="Boho"
            class="age-gate__logo"
          >
          <span class="age-gate__badge">18+</span>
        </div>

        <span class="age-gate__notice">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          {{ $t('loginConsent.ageNotice') }}
        </span>

        <h2
          id="age-gate-title"
          class="age-gate__title"
        >
          {{ $t('loginConsent.ageTitle') }}
        </h2>

        <div class="age-gate__actions">
          <button
            type="button"
            class="age-btn age-btn--light"
            @click="confirmAge"
          >
            {{ $t('loginConsent.ageConfirm') }}
          </button>
          <button
            type="button"
            class="age-btn age-btn--dark"
            @click="leave"
          >
            {{ $t('loginConsent.ageLeave') }}
          </button>
        </div>

        <p class="age-gate__child">
          <a
            href="/legal/child-safety"
            @click.prevent="$emit('open-legal', 'child-safety')"
          >{{ $t('legal.docs.childSafety') }}</a>
          · {{ $t('loginConsent.ageChildText') }}
        </p>

        <nav class="age-gate__links">
          <a
            href="/legal/terms-of-service"
            @click.prevent="$emit('open-legal', 'terms-of-service')"
          >{{ $t('legal.docs.termsOfService') }}</a>
          <a
            href="/legal/privacy-policy"
            @click.prevent="$emit('open-legal', 'privacy-policy')"
          >{{ $t('legal.docs.privacyPolicy') }}</a>
          <a
            href="/legal/child-safety"
            @click.prevent="$emit('open-legal', 'child-safety')"
          >{{ $t('loginConsent.ageChildShort') }}</a>
        </nav>
      </div>
    </div>

    <!-- Çerez tercihleri: yaş onayından sonra, seçim yapılana kadar görünür -->
    <transition name="cookie-slide">
      <div
        v-if="!showAgeGate && showCookie"
        class="cookie-panel"
        role="dialog"
        aria-labelledby="cookie-panel-title"
      >
        <div class="cookie-panel__head">
          <span class="cookie-panel__icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
              <path d="M8.5 8.5v.01" />
              <path d="M16 15.5v.01" />
              <path d="M12 12v.01" />
              <path d="M11 17v.01" />
              <path d="M7 14v.01" />
            </svg>
          </span>
          <div class="cookie-panel__intro">
            <h3
              id="cookie-panel-title"
              class="cookie-panel__title"
            >
              {{ $t('loginConsent.cookieTitle') }}
            </h3>
            <p class="cookie-panel__text">
              {{ $t('loginConsent.cookieText') }}
              <a
                href="/legal/privacy-policy"
                @click.prevent="$emit('open-legal', 'privacy-policy')"
              >{{ $t('loginConsent.cookiePolicy') }}</a>
            </p>
          </div>
          <button
            type="button"
            class="cookie-panel__close"
            :aria-label="$t('loginConsent.close')"
            @click="saveCookie('necessary')"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <ul class="cookie-panel__list">
          <li
            v-for="cat in categories"
            :key="cat.key"
          >
            <div class="cookie-panel__cat">
              <strong>{{ $t(`loginConsent.${cat.key}Title`) }}</strong>
              <small>{{ $t(`loginConsent.${cat.key}Desc`) }}</small>
            </div>
            <label
              class="cookie-switch"
              :class="{ 'is-locked': cat.locked }"
            >
              <input
                v-model="choices[cat.key]"
                type="checkbox"
                :disabled="cat.locked"
                :aria-label="$t(`loginConsent.${cat.key}Title`)"
              >
              <span />
            </label>
          </li>
        </ul>

        <div class="cookie-panel__actions">
          <button
            type="button"
            class="cookie-btn cookie-btn--primary"
            @click="saveCookie('all')"
          >
            {{ $t('loginConsent.cookieAccept') }}
          </button>
          <button
            type="button"
            class="cookie-btn cookie-btn--outline"
            @click="saveCookie('necessary')"
          >
            {{ $t('loginConsent.cookieReject') }}
          </button>
          <button
            type="button"
            class="cookie-btn cookie-btn--dark"
            @click="saveCookie('choices')"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {{ $t('loginConsent.cookieSave') }}
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { $themeConfig } from '@themeConfig'

const COOKIE_KEY = 'boho_cookie_consent'

function readStorage(key) {
  try {
    return window.localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, value)
  } catch (e) {
    // depolama kapalıysa seçim yalnızca bu oturum için geçerli olur
  }
}

export default {
  name: 'LoginConsent',
  data() {
    return {
      logo: $themeConfig.app.appLogoImage,
      // Yaş onayı saklanmaz; giriş sayfası her açıldığında yeniden sorulur
      showAgeGate: true,
      showCookie: !readStorage(COOKIE_KEY),
      categories: [
        { key: 'necessary', locked: true },
        { key: 'functional' },
        { key: 'analytics' },
        { key: 'marketing' },
      ],
      choices: {
        necessary: true,
        functional: false,
        analytics: false,
        marketing: false,
      },
    }
  },
  computed: {
    isRtl() {
      return this.$i18n.locale === 'ar'
    },
  },
  watch: {
    showAgeGate: {
      immediate: true,
      handler(open) {
        // Yaş onayı açıkken arka plandaki sayfa kaydırılmasın
        document.body.style.overflow = open ? 'hidden' : ''
      },
    },
  },
  beforeDestroy() {
    document.body.style.overflow = ''
  },
  methods: {
    confirmAge() {
      this.showAgeGate = false
    },
    leave() {
      window.location.href = 'https://www.google.com'
    },
    // mode: 'all' tümünü kabul, 'necessary' yalnızca zorunlu, 'choices' kullanıcının seçimleri
    saveCookie(mode) {
      const optional = ['functional', 'analytics', 'marketing']
      const consent = { necessary: true }
      optional.forEach(key => {
        if (mode === 'all') consent[key] = true
        else if (mode === 'necessary') consent[key] = false
        else consent[key] = Boolean(this.choices[key])
      })
      consent.date = new Date().toISOString()
      writeStorage(COOKIE_KEY, JSON.stringify(consent))
      this.showCookie = false
      // Etiket yöneticisi (GTM) kullanıyorsa seçimi okuyabilsin
      try {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event: 'cookie_consent', cookieConsent: consent })
      } catch (e) {
        // yoksay
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.age-gate {
  position: fixed;
  inset: 0;
  z-index: 1035; // sözleşme penceresi (bootstrap modal, 1040+) bunun üstünde açılabilsin
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow-y: auto;
  background: rgba(8, 4, 24, 0.85);
  backdrop-filter: blur(10px);
}

.age-gate__card {
  position: relative;
  width: 100%;
  max-width: 480px;
  padding: 34px 32px 28px;
  border-radius: 26px;
  text-align: center;
  color: #fff;
  background:
    radial-gradient(120% 90% at 20% 0%, rgba(255, 255, 255, 0.22), transparent 55%),
    linear-gradient(160deg, #f0389f 0%, #c51a9b 45%, #6d28d9 100%);
  border: 1px solid rgba(255, 255, 255, 0.28);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.age-gate__brand {
  position: relative;
  display: block;
  width: max-content;
  margin: 0 auto 18px;
}

.age-gate__logo {
  height: 88px;
  width: auto;
  display: block;
  // Boho'nun orijinal logosu; pembe zeminde seçilsin diye hafif gölge
  filter: drop-shadow(0 2px 6px rgba(40, 0, 40, 0.35));
}

.age-gate__badge {
  position: absolute;
  top: -10px;
  inset-inline-end: -34px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #ff2d78;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.age-gate__notice {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(70, 10, 60, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.3);
  font-size: 13px;
  font-weight: 700;
}

.age-gate__title {
  margin: 18px 0 22px;
  color: #fff !important;
  font-size: 21px;
  font-weight: 800;
  line-height: 1.35;
}

.age-gate__actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.age-btn {
  width: 100%;
  min-height: 48px;
  padding: 10px 18px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }
}

.age-btn--light {
  border: 0;
  color: #2a0f3a;
  background: #fff6f9;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
}

.age-btn--dark {
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  background: rgba(60, 8, 60, 0.45);
}

.age-gate__child {
  margin: 22px 0 0;
  font-size: 13px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.9);

  a {
    color: #fff;
    text-decoration: underline;
  }
}

.age-gate__links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 22px;
  margin-top: 16px;

  a {
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
}

@media (max-width: 575px) {
  .age-gate__card {
    padding: 28px 20px 22px;
  }

  .age-gate__title {
    font-size: 18px;
  }
}

.cookie-panel {
  position: fixed;
  inset-inline-end: 24px;
  bottom: 24px;
  z-index: 1030;
  width: 420px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: 22px 20px 20px;
  border-radius: 26px;
  background: #fff;
  color: #4a4458;
  box-shadow: 0 24px 60px rgba(10, 4, 30, 0.45);
}

.cookie-panel__head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.cookie-panel__icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #d4148f;
  background: #fde7f4;
}

.cookie-panel__intro {
  flex: 1;
  min-width: 0;
}

.cookie-panel__title {
  margin: 2px 0 6px;
  color: #1c1530 !important;
  font-size: 20px;
  font-weight: 800;
}

.cookie-panel__text {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: #6b6478;

  a {
    display: inline-block;
    margin-top: 2px;
    color: #1c1530;
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
}

.cookie-panel__close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #8a8496;
  background: transparent;
  cursor: pointer;

  &:hover {
    background: #f3f0f7;
  }
}

.cookie-panel__list {
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
  border: 1px solid #ece8f2;
  border-radius: 18px;

  li {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;

    & + li {
      border-top: 1px solid #f1edf6;
    }
  }
}

.cookie-panel__cat {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    color: #1c1530;
    font-size: 15px;
    font-weight: 700;
  }

  small {
    color: #8a8496;
    font-size: 12.5px;
    line-height: 1.45;
  }
}

.cookie-switch {
  position: relative;
  flex-shrink: 0;
  width: 54px;
  height: 30px;
  margin: 0;
  cursor: pointer;

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: #dcd8e2;
    transition: background 0.2s ease;

    &::after {
      content: '';
      position: absolute;
      top: 3px;
      inset-inline-start: 3px;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: #fff;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
      transition: transform 0.2s ease;
    }
  }

  input:checked + span {
    background: linear-gradient(135deg, #a855f7, #ea03b2);
  }

  input:checked + span::after {
    transform: translateX(24px);
  }

  input:focus-visible + span {
    outline: 2px solid #ea03b2;
    outline-offset: 2px;
  }

  &.is-locked {
    cursor: default;

    span {
      opacity: 0.6;
    }
  }
}

[dir='rtl'] .cookie-switch input:checked + span::after {
  transform: translateX(-24px);
}

.cookie-panel__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 18px;
}

.cookie-btn {
  width: 100%;
  height: 50px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }
}

.cookie-btn--primary {
  border: 0;
  color: #fff;
  background: linear-gradient(135deg, #a855f7, #ea03b2);
  box-shadow: 0 8px 20px rgba(234, 3, 178, 0.3);
}

.cookie-btn--outline {
  border: 1px solid #d9d4e1;
  color: #1c1530;
  background: #fff;
}

.cookie-btn--dark {
  border: 0;
  color: #fff;
  background: #1c1530;
}

.cookie-slide-enter-active,
.cookie-slide-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.cookie-slide-enter,
.cookie-slide-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

// Telefonda alttan açılan panel
@media (max-width: 575px) {
  .cookie-panel {
    left: 0;
    right: 0;
    bottom: 0;
    width: auto;
    max-width: none;
    max-height: 88vh;
    border-radius: 24px 24px 0 0;
    padding: 20px 16px calc(16px + env(safe-area-inset-bottom, 0px));
  }

  .cookie-panel__title {
    font-size: 18px;
  }
}
</style>
